import { useMentorStudentMeetings } from '@actions';
import { ReactComponent as EmptyStateIcon } from '@assets/icons/V4/empty-states/sad-calendar.svg';
import { MeetingModal } from '@components';
import MeetingNeedingFeedbackMentor from '@components/V3/MeetingsNeedingFeedbackMentor/MeetingNeedingFeedbackMentor';
import NextMeeting from '@components/V3/NextMeeting';
import NextMeetingMentor from '@components/V3/NextMeetingsMentor/NextMeetingMentor';
import Button from '@components/V4/Button';
import SkeletonBox from '@components/V4/SkeletonBox';
import { useAuthState } from '@contexts/AuthContext';
import { useModal } from '@hooks';
import FeedbackModal, { Feedback } from '@modules/Mentor/components/FeedbackModal/FeedbackModal';
import { UserProfileDTO } from '@shared/clientTypes/DTOs/UserDTO';
import { MeetingWithHomework } from '@shared/common';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ProfileContext } from '../../AccountProfile';
import ProfileMainSection from '../ResumeSection';

interface SessionsTabProps {
  userProfile?: Partial<UserProfileDTO>;
}

const SessionsTab = (props: SessionsTabProps) => {
  const { userProfile } = props;
  const { userId: mentorId } = useAuthState();
  const studentId = userProfile?._id ?? '';
  const canQueryMeetings = !!studentId;
  const [Modal, show] = useModal(MeetingModal);
  const navigate = useHistory();
  const location = useLocation();

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackModalData, setFeedbackModalData] = useState({
    id: '',
    feedback: {} as Feedback,
    title: '',
  });

  const { isLoading: isProfileLoading } = useContext(ProfileContext);

  const [filters, setFilters] = useState([
    {
      text: 'All',
      value: '',
    },
  ]);

  const [currentFilter, setCurrentFilter] = useState(filters[0]);

  const { data: meetings, isLoading: isLoadingMeetings } = useMentorStudentMeetings(
    {
      studentId: userProfile?._id ?? '',
      enrollmentId: currentFilter.value,
    },
    {
      enabled: canQueryMeetings,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (!userProfile?.enrollments) return;

    const newFilters = Array.from(
      new Map(
        userProfile.enrollments
          ?.filter((enrollment) => enrollment.mentor?._id === mentorId)
          .map((enrollment) => [
            enrollment._id,
            {
              text: enrollment.course?.title ?? '',
              value: enrollment._id ?? '',
            },
          ])
      ).values()
    );

    const allFilter = { text: 'All', value: '' };
    const fullFilters = [allFilter, ...newFilters];
    setFilters(fullFilters);

    const params = new URLSearchParams(location.search);
    const initialEnrollmentId = params.get('enrollmentId') ?? '';

    const candidate = fullFilters.find((f) => f.value === initialEnrollmentId) ?? allFilter;

    if (candidate.value !== currentFilter.value) {
      setCurrentFilter(candidate);
    }
  }, [userProfile?.enrollments, mentorId, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (currentFilter.value) {
      params.set('enrollmentId', currentFilter.value);
    } else {
      params.delete('enrollmentId');
    }

    const newSearch = params.toString();
    const currentSearch = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;

    if (newSearch !== currentSearch) {
      navigate.push({ search: newSearch ? `?${newSearch}` : '' }, { replace: true });
    }
  }, [currentFilter, navigate, location.search]);

  const hasMeetings =
    !!meetings?.needFeedbackMeetings.length ||
    !!meetings?.ongoingMeetings.length ||
    !!meetings?.pastMeetings.length ||
    !!meetings?.upcomingMeetings.length;

  return (
    <section className="flex flex-col gap-4 ">
      <ProfileMainSection title="All Sessions">
        {Modal}
        <div className="flex flex-wrap gap-4">
          {isProfileLoading ? (
            <>
              {[...Array(10).keys()].map((_, index) => (
                <SkeletonBox key={index} className="h-[29px] w-36 rounded-full" />
              ))}
            </>
          ) : (
            filters.map((filter) => (
              <p
                key={filter.value}
                onClick={() => setCurrentFilter(filter)}
                className={mergeClassNames(
                  'cursor-pointer py-1 px-4 bg-surfaceHover rounded-full',
                  {
                    'bg-customPrimary text-black': filter.value === currentFilter.value,
                  }
                )}
              >
                {filter.text}
              </p>
            ))
          )}
        </div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold"></h3>
          <Button
            variant="primary"
            buttonText="Add Meeting"
            onClick={() =>
              show({
                date: moment(),
                isOnHomePage: true,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          {isLoadingMeetings && (
            <>
              {[...Array(10).keys()].map((_, index) => (
                <SkeletonBox key={index} className="h-[72px] w-full rounded-2xl" />
              ))}
            </>
          )}
          {!isLoadingMeetings &&
            (hasMeetings ? (
              <>
                {meetings?.ongoingMeetings?.map((meeting: MeetingWithHomework) => (
                  <NextMeetingMentor isUserProfileView key={meeting._id} meeting={meeting} />
                ))}
                {meetings?.upcomingMeetings?.map((meeting: MeetingWithHomework) => (
                  <NextMeetingMentor isUserProfileView key={meeting._id} meeting={meeting} />
                ))}
                {meetings?.needFeedbackMeetings?.map((meeting: MeetingWithHomework) => (
                  <MeetingNeedingFeedbackMentor
                    isUserProfileView
                    key={meeting._id}
                    meeting={meeting}
                    setShowFeedbackModal={setShowFeedbackModal}
                    setFeedbackModalData={setFeedbackModalData}
                  />
                ))}
                {meetings?.pastMeetings?.map((meeting: MeetingWithHomework) => (
                  <NextMeeting
                    key={meeting._id}
                    meeting={meeting}
                    otherName={meeting.students[0]?.fullName || ''}
                    isUserProfileView
                    hideJoinBtn
                    isPastMeeting
                  />
                ))}
              </>
            ) : (
              <EmptyStateCard
                icon={<EmptyStateIcon />}
                text={
                  <p>
                    You have no meetings lined up...
                    <br />
                    Keep things rolling by scheduling your next session with your students!
                  </p>
                }
              />
            ))}
        </div>
      </ProfileMainSection>

      {showFeedbackModal && (
        <FeedbackModal
          meetingId={feedbackModalData.id}
          feedback={feedbackModalData.feedback}
          setFeedback={setFeedbackModalData}
          title={feedbackModalData.title}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}
    </section>
  );
};

export default SessionsTab;
